import { AlertTriangle, Maximize2, Package, PackageCheck, Truck } from "lucide-react";
import { useState } from "react";

import { EmptyState } from "@/components/cinch/EmptyState";
import { ImageLightbox } from "@/components/cinch/ImageLightbox";
import { KpiTile } from "@/components/cinch/KpiTile";
import { LinkOut } from "@/components/cinch/LinkOut";
import { StatusBadge } from "@/components/cinch/StatusBadge";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

import {
  CARRIER_LABEL,
  SHIPMENT_LABEL,
  fmtDate,
  fmtMoney,
  fmtMoney0,
  useSummary,
  type ItemStock,
  type ShipmentTracking,
} from "../api";

/**
 * Summary tab — "what have we got, and where are the boxes".
 *
 * Stock reads as cards rather than a table because the photo is load-bearing:
 * "the grey blanket" and "the blue one" are the same catalog row, and a picture
 * is how you know which is on the shelf.
 */
export function SummaryView() {
  const { data, isLoading, error } = useSummary();

  if (isLoading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-28" />
        ))}
      </div>
    );
  }
  if (error) {
    return <Alert variant="danger">Could not load swag stock: {String(error)}</Alert>;
  }
  if (!data) return null;

  const onHand = data.stock.reduce((sum, s) => sum + s.on_hand, 0);
  const free = data.stock.reduce((sum, s) => sum + s.free, 0);
  const earmarked = data.stock.reduce((sum, s) => sum + s.reserved, 0);
  const inFlight = data.shipments.filter((s) => s.status !== "delivered").length;
  const lowStock = data.stock.filter((s) => s.low_stock && s.on_hand > 0);

  return (
    <div className="flex flex-col gap-5">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiTile
          label="Units on hand"
          value={onHand.toLocaleString()}
          sub={`${free.toLocaleString()} free to give out`}
          info="On hand is every unit bought minus every unit given away. Free excludes units earmarked to a department and units promised to a request awaiting approval."
          infoTitle="On hand vs free"
        />
        <KpiTile
          label="Value on hand"
          value={fmtMoney0(data.total_value_on_hand)}
          sub="At the price we paid per unit"
        />
        <KpiTile
          label="Earmarked"
          value={earmarked.toLocaleString()}
          sub={earmarked ? "Claimed by a department" : "Nothing set aside"}
          tone={earmarked ? "warning" : "neutral"}
        />
        <KpiTile
          label="In flight"
          value={inFlight.toLocaleString()}
          sub={`${data.pending_requests} request${data.pending_requests === 1 ? "" : "s"} awaiting approval`}
          tone={inFlight ? "warning" : "neutral"}
        />
      </div>

      {lowStock.length > 0 && (
        <Alert variant="warning" className="flex items-start gap-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Running low:{" "}
            {lowStock.map((s, i) => (
              <span key={s.item_id}>
                {i > 0 && ", "}
                <strong>{s.item_name}</strong> ({s.on_hand} left)
              </span>
            ))}
            . Time to reorder.
          </span>
        </Alert>
      )}

      <section className="flex flex-col gap-3">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <Package className="h-4 w-4" /> What we can give out
        </h2>
        {data.stock.length === 0 ? (
          <EmptyState icon={<Package />} title="No swag loaded yet">
            An admin can add purchases on the Admin tab — or import the old
            spreadsheet in one go.
          </EmptyState>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {data.stock.map((item) => (
              <StockCard key={item.item_id} item={item} />
            ))}
          </div>
        )}
      </section>

      <section className="flex flex-col gap-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="flex items-center gap-2 text-sm font-semibold">
            <Truck className="h-4 w-4" /> Shipments
          </h2>
          <p className="text-xs text-muted-foreground">
            In flight, plus anything delivered in the last{" "}
            {data.shipment_window_days} days.
            {data.tracking_manual_only ? (
              <>
                {" "}
                Statuses are entered by hand — no carrier API key is configured
                yet, so nothing polls automatically.
              </>
            ) : (
              <> Auto-checked nightly via {data.tracking_providers.join(", ")}.</>
            )}
          </p>
        </div>
        {data.shipments.length === 0 ? (
          <EmptyState icon={<Truck />} title="Nothing in the mail">
            Boxes appear here while they’re in flight, and drop off {data.shipment_window_days} days after
            they land. The Detail tab keeps them forever.
          </EmptyState>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {data.shipments.map((shipment) => (
              <ShipmentCard key={shipment.id} shipment={shipment} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function StockCard({ item }: { item: ItemStock }) {
  const [zoomed, setZoomed] = useState(false);
  // Tracks the URL that failed (not a boolean) so a re-uploaded photo on the
  // same card gets a fresh chance to load.
  const [brokenUrl, setBrokenUrl] = useState<string | null>(null);
  const photo = item.photo_url != null && item.photo_url !== brokenUrl ? item.photo_url : null;

  return (
    <Card className="overflow-hidden">
      {photo != null ? (
        // A card-width thumbnail can't answer "which blanket is that?", which
        // is the question the photo is here to answer — so it opens.
        <button
          type="button"
          onClick={() => setZoomed(true)}
          aria-label={`View ${item.item_name} larger`}
          className="group relative block h-36 w-full cursor-zoom-in overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
        >
          <img
            src={photo}
            alt={item.item_name}
            onError={() => setBrokenUrl(photo)}
            className="h-36 w-full bg-muted object-cover transition-transform duration-200 group-hover:scale-[1.03]"
          />
          <span className="absolute right-2 top-2 rounded-full bg-black/45 p-1.5 text-white opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
            <Maximize2 className="h-3.5 w-3.5" />
          </span>
        </button>
      ) : (
        <div className="flex h-36 w-full items-center justify-center bg-muted text-muted-foreground">
          <Package className="h-8 w-8" />
        </div>
      )}
      {zoomed && photo != null && (
        <ImageLightbox
          url={photo}
          alt={item.item_name}
          caption={`${item.item_name} — ${item.on_hand} on hand${
            item.unit_cost != null ? `, ${fmtMoney(item.unit_cost)} each` : ""
          }`}
          onClose={() => setZoomed(false)}
        />
      )}
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-base">{item.item_name}</CardTitle>
          {item.low_stock && item.on_hand > 0 && <StatusBadge value="low_stock" />}
          {item.on_hand === 0 && <StatusBadge value="out_of_stock" />}
        </div>
        <p className="text-xs text-muted-foreground">
          {item.unit_cost != null ? `${fmtMoney(item.unit_cost)} each · ` : ""}
          {fmtMoney(item.value_on_hand)} on the shelf
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-2 pb-4">
        <div className="grid grid-cols-3 gap-2">
          <KpiTile bare label="On hand" value={item.on_hand} />
          <KpiTile bare label="Free" value={item.free} tone="positive" />
          <KpiTile bare label="Given" value={item.disbursed} tone="neutral" />
        </div>
        {(item.reserved > 0 || item.pending > 0) && (
          <div className="flex flex-wrap gap-1 border-t pt-2">
            {item.reservations.map((r) => (
              <Badge key={r.department_id} variant="info">
                {r.department_name} · {r.quantity}
              </Badge>
            ))}
            {item.pending > 0 && (
              <Badge variant="warning">{item.pending} awaiting approval</Badge>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function ShipmentCard({ shipment }: { shipment: ShipmentTracking }) {
  const delivered = shipment.status === "delivered";
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-base">
            {shipment.recipients.length > 0
              ? shipment.recipients.join(", ")
              : (shipment.ship_to_name ?? "Unnamed recipient")}
          </CardTitle>
          <StatusBadge
            value={shipment.status}
            label={SHIPMENT_LABEL[shipment.status]}
          />
        </div>
        <p className="text-xs text-muted-foreground">
          {shipment.contents.join(", ") || "No contents recorded"}
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-2 pb-4 text-sm">
        {shipment.ship_to_address && (
          <p className="text-xs text-muted-foreground">{shipment.ship_to_address}</p>
        )}
        <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
          <Row label="Carrier">
            {shipment.carrier ? CARRIER_LABEL[shipment.carrier] : "—"}
            {shipment.carrier_service ? ` · ${shipment.carrier_service}` : ""}
          </Row>
          <Row label="Shipping">{fmtMoney(shipment.shipping_cost)}</Row>
          <Row label="Shipped">{fmtDate(shipment.shipped_at)}</Row>
          <Row label={delivered ? "Delivered" : "In transit"}>
            {delivered
              ? fmtDate(shipment.delivered_at)
              : shipment.days_in_transit != null
                ? `${shipment.days_in_transit} day${shipment.days_in_transit === 1 ? "" : "s"}`
                : "—"}
          </Row>
        </dl>
        {shipment.status_detail && (
          <p className="flex items-start gap-1 text-xs text-muted-foreground">
            <PackageCheck className="mt-0.5 h-3 w-3 shrink-0" />
            {shipment.status_detail}
          </p>
        )}
        {shipment.check_error && (
          <p className="text-xs text-warning">
            Last automatic check: {shipment.check_error}
          </p>
        )}
        {shipment.tracking_number && (
          <LinkOut href={shipment.tracking_url} size="xs">
            {shipment.tracking_number}
          </LinkOut>
        )}
      </CardContent>
    </Card>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right font-medium">{children}</dd>
    </>
  );
}
